import React, { useState } from 'react';
import { Loader2, Check, Truck, CreditCard, Package, ClipboardCheck } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { useToast } from '@/components/ui/use-toast';

// Admin fulfilment stage advancement controls. Renders a horizontal 4-step
// indicator (1→2→3→4) with clickable advance buttons. Calls the
// b2bAdvanceFulfilmentStage backend function which updates the stage and
// sends the appropriate customer + warehouse emails.

const STAGES = [
  { num: 1, label: 'Order Received', icon: ClipboardCheck, color: 'blue' },
  { num: 2, label: 'Payment Authorised', icon: CreditCard, color: 'green' },
  { num: 3, label: 'Picked & Packaged', icon: Package, color: 'cyan' },
  { num: 4, label: 'Dispatched', icon: Truck, color: 'purple' },
];

const COLOR_CLASSES = {
  blue: { active: 'border-blue-400 bg-blue-400/10 text-blue-400', done: 'border-blue-400/40 bg-blue-400/5 text-blue-400/70', btn: 'border-blue-400/30 bg-blue-400/10 hover:bg-blue-400 hover:text-black text-blue-400' },
  green: { active: 'border-green-400 bg-green-400/10 text-green-400', done: 'border-green-400/40 bg-green-400/5 text-green-400/70', btn: 'border-green-400/30 bg-green-400/10 hover:bg-green-400 hover:text-black text-green-400' },
  cyan: { active: 'border-cyan-400 bg-cyan-400/10 text-cyan-400', done: 'border-cyan-400/40 bg-cyan-400/5 text-cyan-400/70', btn: 'border-cyan-400/30 bg-cyan-400/10 hover:bg-cyan-400 hover:text-black text-cyan-400' },
  purple: { active: 'border-purple-400 bg-purple-400/10 text-purple-400', done: 'border-purple-400/40 bg-purple-400/5 text-purple-400/70', btn: 'border-purple-400/30 bg-purple-400/10 hover:bg-purple-400 hover:text-black text-purple-400' },
};

export default function FulfilmentStageControls({ order, onStageAdvanced }) {
  const { toast } = useToast();
  const [advancing, setAdvancing] = useState(null);
  const currentStage = Number(order.fulfilment_stage) || 1;

  const advance = async (targetStage) => {
    setAdvancing(targetStage);
    try {
      const res = await base44.functions.invoke('b2bAdvanceFulfilmentStage', {
        order_id: order.id,
        target_stage: targetStage,
      });
      if (!res?.data?.success) throw new Error(res?.data?.error || 'Failed to advance stage');
      toast({
        title: `Stage ${targetStage} advanced`,
        description: res.data.message?.includes('no email') ? 'Stage already set — no email re-sent' : `${STAGES[targetStage - 1].label} — email sent to customer`,
      });
      if (onStageAdvanced) onStageAdvanced({ ...order, fulfilment_stage: targetStage });
    } catch (e) {
      toast({ title: 'Failed to advance stage', description: e?.message || 'Unknown error', variant: 'destructive' });
    }
    setAdvancing(null);
  };

  return (
    <div className="border border-white/10 bg-[#0d0d0f] p-4">
      <p className="text-white/40 text-[10px] font-bold tracking-widest mb-3">FULFILMENT STAGE — CUSTOMER NOTIFICATIONS</p>
      <div className="flex items-center gap-1 sm:gap-2">
        {STAGES.map((stage, idx) => {
          const Icon = stage.icon;
          const colors = COLOR_CLASSES[stage.color];
          const isDone = currentStage > stage.num;
          const isActive = currentStage === stage.num;
          const isFuture = currentStage < stage.num;
          const canAdvance = currentStage === stage.num - 1 || (currentStage < stage.num && stage.num === currentStage + 1);

          return (
            <React.Fragment key={stage.num}>
              <div className="flex flex-col items-center gap-1.5 flex-1 min-w-0">
                <div
                  className={`w-9 h-9 rounded-full border-2 flex items-center justify-center transition-colors ${
                    isActive ? colors.active
                    : isDone ? colors.done
                    : 'border-white/10 text-white/20'
                  }`}
                >
                  {isDone ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                </div>
                <p className={`text-[9px] font-bold tracking-wider text-center leading-tight ${isActive ? 'text-white' : isDone ? 'text-white/50' : 'text-white/25'}`}>
                  {stage.num}. {stage.label.toUpperCase()}
                </p>
                {isFuture && canAdvance && (
                  <button
                    onClick={() => advance(stage.num)}
                    disabled={advancing !== null}
                    className={`inline-flex items-center gap-1 border text-[9px] font-bold tracking-widest px-2 py-1 transition-colors disabled:opacity-50 ${colors.btn}`}
                  >
                    {advancing === stage.num ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                    ADVANCE
                  </button>
                )}
                {isActive && (
                  <span className="text-[9px] font-bold tracking-widest text-white/40">CURRENT</span>
                )}
              </div>
              {idx < STAGES.length - 1 && (
                <div className={`h-0.5 w-4 sm:w-8 shrink-0 ${currentStage > stage.num ? 'bg-white/20' : 'bg-white/5'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
      {/* Quick-advance buttons for the next stage */}
      {currentStage < 4 && (
        <div className="mt-3 pt-3 border-t border-white/5 flex flex-wrap gap-2">
          {STAGES.filter((s) => s.num > currentStage).map((stage) => {
            const Icon = stage.icon;
            const colors = COLOR_CLASSES[stage.color];
            const isNext = stage.num === currentStage + 1;
            return (
              <button
                key={stage.num}
                onClick={() => advance(stage.num)}
                disabled={advancing !== null}
                className={`inline-flex items-center gap-1.5 border font-bold text-[10px] tracking-widest px-3 py-1.5 transition-colors disabled:opacity-50 ${isNext ? colors.btn : 'border-white/10 text-white/30 hover:text-white/50'}`}
              >
                {advancing === stage.num ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
                MARK {stage.label.toUpperCase()}
              </button>
            );
          })}
        </div>
      )}
      {currentStage === 4 && (
        <p className="mt-3 pt-3 border-t border-white/5 text-center text-green-400/70 text-[10px] font-bold tracking-widest">
          ✓ ORDER COMPLETE — ALL STAGES ADVANCED
        </p>
      )}
    </div>
  );
}